import { Overlay } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  DateFilterFn,
  MAT_DATEPICKER_SCROLL_STRATEGY,
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { FORMATS } from '../../constants/datepicker.constant';
import { InputDirective } from '../../directives/input/input.directive';
import { dateToLocalString } from '../../utils/date.utils';
import { DatepickerHeaderComponent } from '../datepicker-header/datepicker-header.component';

@Component({
  selector: 'csl-datepicker',
  standalone: true,
  imports: [CommonModule, MatDatepickerModule, MatInputModule, InputDirective],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None, // We need to override material css variables on the :root level
  providers: [
    provideNativeDateAdapter(FORMATS),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
    {
      provide: MAT_DATEPICKER_SCROLL_STRATEGY,
      useFactory: () => {
        const overlay = inject(Overlay);
        return () => overlay.scrollStrategies.close();
      },
    },
  ],
  host: {
    class: 'block',
  },
})
export class DatepickerComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);
  DatepickerHeaderComponent = DatepickerHeaderComponent;
  private readonly fallbackId = `csl-datepicker-${Math.random().toString(36).slice(2, 10)}`;

  @ViewChild('dateInput') private dateInput!: ElementRef<HTMLInputElement>;

  @Input() isDisabled = false;
  @Input() placeholder = '';
  @Input() id?: string;
  @Input() ariaLabel?: string;
  @Input() ariaLabelledby?: string;
  @Input() ariaDescribedby?: string | null;
  @Input() ariaInvalid?: string | null;
  @Input() stringOutput? = true;
  @Input() required = false;
  @Input() inverted = false;
  @Input() datepickerFilter: DateFilterFn<Date | null> = () => true;
  private _minDate: Date | null = null;
  @Input()
  public get minDate(): Date | null {
    return this._minDate;
  }
  public set minDate(value: Date | string | null) {
    this._minDate = typeof value === 'string' ? new Date(value) : value;
  }

  private _maxDate: Date | null = null;
  @Input()
  public get maxDate(): Date | null {
    return this._maxDate;
  }
  public set maxDate(value: Date | string | null) {
    this._maxDate = typeof value === 'string' ? new Date(value) : value;
  }

  value: Date | null = null;

  public get inputId(): string {
    return this.id?.trim() || this.fallbackId;
  }

  onChange = (date: Date | string | null) => {
    /*  */
  };
  onTouched = () => {
    /*  */
  };

  writeValue(value: Date | string | null): void {
    this.value = typeof value === 'string' ? new Date(value) : value;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onDateChange(event: MatDatepickerInputEvent<Date, Date | null>): void {
    const date = event.value;
    this.value = date;
    if (this.stringOutput) {
      this.onChange(date ? dateToLocalString(date) : null);
    } else {
      this.onChange(date);
    }
  }

  onInputClick(picker: MatDatepicker<Date>): void {
    if (picker.opened) {
      picker.close();
      return;
    }

    picker.open();
  }

  onInputKeydown(event: KeyboardEvent, picker: MatDatepicker<Date>): void {
    // Keep tab navigation working; only block manual typing.
    if (event.key === 'Tab' || event.key === 'Shift' || event.key === 'Escape') {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (picker.opened) {
        picker.close();
      } else {
        picker.open();
      }
      return;
    }

    event.preventDefault();
  }

  onPickerClosed(): void {
    this.onTouched();
    this.dateInput.nativeElement.focus();
  }
}
